/**
 * How a failed manual save reads (plan 5.1, 5.2), shared by the page op
 * queue, the document saves, the Code tab and the suggestions card:
 *
 * - `conflict`: 409 `ARTIFACT_VERSION_CONFLICT`, the base is not the head
 *   any more. Nothing merges in Phase 2: the editor offers Reload.
 * - `invalid`: 422 `ARTIFACT_EDIT_INVALID` (or 409 with `details.editor`, a
 *   file of another kind). The server's message is already in the user's
 *   language and is shown as it is; sending the same change again cannot
 *   work.
 * - `limited`: 429 `ARTIFACT_LIMIT_REACHED`, the hourly cap of manual saves.
 *   Shown as it is; the change is kept for a later Try again.
 * - `disabled`: 403 `ARTIFACT_EDITS_DISABLED`; `gone`: the file or version
 *   is not there any more.
 * - `retryable`: the network or anything else; retried, then Try again.
 */
export type SaveFailureKind = 'conflict' | 'invalid' | 'limited' | 'disabled' | 'gone' | 'retryable';

export type SaveFailure = {
  kind: SaveFailureKind;
  /** The server's words, when they are meant for the user (422, 429). */
  message: string | null;
  /** 409: the head the server has now. */
  headVersionId: string | null;
};

function field(error: unknown, name: string): unknown {
  return error && typeof error === 'object' ? (error as Record<string, unknown>)[name] : undefined;
}

export function classifySaveError(error: unknown): SaveFailure {
  const code = typeof field(error, 'code') === 'string' ? (field(error, 'code') as string) : '';
  const message = typeof field(error, 'message') === 'string' ? (field(error, 'message') as string) : '';
  const details = field(error, 'details');
  const detail = (name: string) =>
    details && typeof details === 'object' ? (details as Record<string, unknown>)[name] : undefined;
  const head = detail('headVersionId');
  switch (code) {
    case 'ARTIFACT_VERSION_CONFLICT':
      return { kind: 'conflict', message: null, headVersionId: typeof head === 'string' ? head : null };
    case 'ARTIFACT_EDIT_INVALID':
      return { kind: 'invalid', message: message || null, headVersionId: null };
    case 'ARTIFACT_LIMIT_REACHED':
      return { kind: 'limited', message: message || null, headVersionId: null };
    case 'ARTIFACT_EDITS_DISABLED':
      return { kind: 'disabled', message: message || null, headVersionId: null };
    case 'ARTIFACT_NOT_FOUND':
    case 'ARTIFACT_VERSION_NOT_FOUND':
      return { kind: 'gone', message: null, headVersionId: null };
    default:
      return { kind: 'retryable', message: null, headVersionId: null };
  }
}
